import { buildIdentityHeaders, buildUserContext } from './userContext';

/**
 * Authentication Service (production).
 * Handles user authentication state and API interactions for Azure Static Web Apps.
 * No dev bypass logic; used in production and as base for dev wrapper.
 */
class AuthServiceBase {
    constructor() {
        this.userInfo = null;
        this.isAuthenticated = false;
    }

    /**
     * Always false in production. Exists so callers (e.g. Header) can check without branching.
     * @returns {boolean}
     */
    isAuthBypassEnabled() {
        return false;
    }

    /**
     * Get current user information from Azure Static Web Apps
     * @returns {Promise<Object|null>} User information or null if not authenticated
     */
    async getUserInfo() {
        try {
            const response = await fetch('/.auth/me');
            const data = await response.json();

            if (data && data.clientPrincipal && data.clientPrincipal.identityProvider === 'aad') {
                this.userInfo = data.clientPrincipal;
                this.isAuthenticated = true;
                return this.userInfo;
            }
            this.userInfo = null;
            this.isAuthenticated = false;
            return null;
        } catch (error) {
            console.error('Error fetching user info:', error);
            this.userInfo = null;
            this.isAuthenticated = false;
            return null;
        }
    }

    /**
     * Check if user is currently authenticated
     * @returns {Promise<boolean>} Authentication status
     */
    async checkAuthentication() {
        const userInfo = await this.getUserInfo();
        return userInfo !== null;
    }

    getCurrentUser() {
        return this.userInfo;
    }

    getAuthenticationStatus() {
        return this.isAuthenticated;
    }

    login() {
        window.location.href = '/.auth/login/aad';
    }

    logout() {
        window.location.href = '/.auth/logout';
    }

    getUserEmail() {
        return this.getUserContext()?.email || this.userInfo?.userDetails || null;
    }

    getUserRoles() {
        return this.userInfo?.userRoles || [];
    }

    getUserGroups() {
        return this.getUserContext()?.groups || [];
    }

    /**
     * Compact Entra/SWA identity used for API activity logging.
     * Includes email, display name, object id, tenant, SWA roles, and Entra groups.
     * @returns {object|null}
     */
    getUserContext() {
        return buildUserContext(this.userInfo);
    }

    /**
     * Request headers carrying the logged-in user. Backends that are reached
     * via SWA `/api/*` should still treat `x-ms-client-principal` as source of
     * truth; these headers cover APIs called directly (e.g. APIM portal).
     * @returns {Record<string, string>}
     */
    getIdentityHeaders() {
        return buildIdentityHeaders(this.getUserContext());
    }

    hasRole(role) {
        return this.getUserRoles().includes(role);
    }
}

export default AuthServiceBase;
